import { useChartId, useDrawingArea, useXScale } from "@mui/x-charts/hooks";
import { AnimatedLine, type AnimatedLineProps } from "@mui/x-charts/LineChart";
// (v9: line ownerState identifies the series via `seriesId`, not `id`)
import { type FC } from "react";

import { useCoreChart } from "~/feature/chart/providers/CoreChartProvider";
import { useVolumeHover } from "~/feature/chart/providers/VolumeHoverProvider";

import {
  DEFAULT_LINE_WIDTH,
  FUTURE_LINE_OPACITY,
  LINE_FUTURE_CLASS,
  LINE_PAST_CLASS,
} from "./constants";

/** Keeps the clip rects from shaving thick stroke caps at the plot edges. */
const CLIP_PADDING = 4;

/**
 * Line slot that splits every series at the hovered bucket: the past segment
 * paints bold on top, the future segment dims — the emphasis the ticket asks
 * for, applied to the lines only (grid, news markers and watermark stay put).
 */
export const SplitLine: FC<AnimatedLineProps> = (props) => {
  const { timeline } = useCoreChart();
  const { hoveredIndex } = useVolumeHover();
  const xScale = useXScale();
  const drawingArea = useDrawingArea();
  const chartId = useChartId();

  const timestamp = hoveredIndex === null ? undefined : timeline[hoveredIndex];

  if (timestamp === undefined) {
    return <AnimatedLine {...props} />;
  }

  const { ownerState, skipAnimation, ...pathProps } = props;

  void skipAnimation;

  const cutX = xScale(new Date(timestamp)) ?? drawingArea.left;
  const clipId = `${chartId}-${ownerState.seriesId}-split`;
  const top = drawingArea.top - CLIP_PADDING;
  const height = drawingArea.height + 2 * CLIP_PADDING;
  const right = drawingArea.left + drawingArea.width;

  const segment = (
    <path
      {...pathProps}
      stroke={ownerState.color}
      strokeWidth={DEFAULT_LINE_WIDTH}
      strokeLinejoin="round"
      fill="none"
    />
  );

  return (
    <g>
      <defs>
        <clipPath id={`${clipId}-past`}>
          <rect
            x={drawingArea.left}
            y={top}
            width={Math.max(0, cutX - drawingArea.left)}
            height={height}
          />
        </clipPath>
        <clipPath id={`${clipId}-future`}>
          <rect
            x={cutX}
            y={top}
            width={Math.max(0, right - cutX)}
            height={height}
          />
        </clipPath>
      </defs>

      <g
        className={LINE_FUTURE_CLASS}
        clipPath={`url(#${clipId}-future)`}
        opacity={FUTURE_LINE_OPACITY}
      >
        {segment}
      </g>
      <g className={LINE_PAST_CLASS} clipPath={`url(#${clipId}-past)`}>
        {segment}
      </g>
    </g>
  );
};
